import React, { useState, useEffect } from 'react';
import { getAuth } from 'firebase/auth';
import { motion, AnimatePresence } from 'framer-motion';
import { ExclamationCircleIcon } from '@heroicons/react/24/outline';

const CLOUD_RUN_SEAWOLF_URL = 'https://cognidoor-backend-92219505867.us-east1.run.app';

function normalizeSolverMicrobes(rawMicrobes = []) {
  return rawMicrobes.map((microbe, index) => ({
    ...microbe,
    id: microbe?.id ?? index + 1,
    solverId: Number.isInteger(microbe?.solverId) ? microbe.solverId : index + 1,
  }));
}

function formatApiError(errorData, fallbackStatus) {
  if (!errorData) {
    return `Cloud Run API failed with status ${fallbackStatus}`;
  }

  if (typeof errorData === 'string') {
    return errorData;
  }

  if (Array.isArray(errorData?.error)) {
    return errorData.error
      .map(item => item?.msg || item?.message || JSON.stringify(item))
      .join('; ');
  }

  if (Array.isArray(errorData?.detail)) {
    return errorData.detail
      .map(item => item?.msg || item?.message || JSON.stringify(item))
      .join('; ');
  }

  return errorData?.error || errorData?.detail || errorData?.message || `Cloud Run API failed with status ${fallbackStatus}`;
}

// Add SolveEcosystemButton component
const SolveEcosystemButton = ({ onSolve, isLoading, progress }) => {
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="mt-8 flex justify-center"
    >
      <motion.button
        whileHover={{ scale: 1.02 }}
        whileTap={{ scale: 0.98 }}
        onClick={onSolve}
        disabled={isLoading}
        className={`
          relative overflow-hidden
          px-8 py-4 sm:px-6 sm:py-3
          rounded-xl
          bg-gradient-to-r from-blue-600/20 to-blue-800/20
          border border-blue-500/30
          backdrop-blur-sm
          ${isLoading ? 'cursor-wait' : 'cursor-pointer'}
          group
          w-full sm:w-full md:w-auto
        `}
      >
        {/* Knight Rider effect */}
        <motion.div
          initial={{ x: '-100%' }}
          animate={{ x: '100%' }}
          transition={{
            repeat: Infinity,
            duration: 2,
            ease: "linear"
          }}
          className="absolute inset-0 bg-gradient-to-r from-transparent via-blue-500/20 to-transparent"
        />

        {/* Glow effect */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: [0, 0.5, 0] }}
          transition={{
            repeat: Infinity,
            duration: 2,
            ease: "easeInOut"
          }}
          className="absolute inset-0 bg-blue-500/10 blur-xl"
        />

        {/* Button content */}
        <div className="relative flex items-center gap-3">
          <span className="text-lg sm:text-base font-semibold text-blue-300">
            {isLoading ? 'Solving Ecosystem...' : 'Solve Ecosystem'}
          </span>
          
          {isLoading && (
            <motion.div
              initial={{ width: 0 }}
              animate={{ width: '100px' }}
              className="h-2 bg-blue-500/30 rounded-full overflow-hidden w-24 sm:w-16"
            >
              <motion.div
                initial={{ x: '-100%' }}
                animate={{ x: '100%' }}
                transition={{
                  repeat: Infinity,
                  duration: 1,
                  ease: "linear"
                }}
                className="h-full w-1/2 bg-blue-500"
              />
            </motion.div>
          )}
        </div>

        {/* Progress indicator */}
        {isLoading && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="absolute -bottom-6 left-0 right-0 text-center text-sm text-blue-400"
          >
            {progress}%
          </motion.div>
        )}
      </motion.button>
    </motion.div>
  );
};

const ManualEntry = ({ initialData, onDataChange, onStateChange, setActiveStep }) => {
  // New state structure with empty initial values
  const [siteNumber, setSiteNumber] = useState(initialData?.siteNumber || '');
  const [attributes, setAttributes] = useState(initialData?.attributes || [
    { name: '', lower: '', upper: '' },
    { name: '', lower: '', upper: '' },
    { name: '', lower: '', upper: '' }
  ]);
  const [traitCount, setTraitCount] = useState(initialData?.traitCount || (initialData?.traitNames?.length > 0 ? initialData.traitNames.length : ''));
  const [traitNames, setTraitNames] = useState(initialData?.traitNames || []);
  const [desiredTrait, setDesiredTrait] = useState(initialData?.desiredTrait || '');
  const [undesiredTrait, setUndesiredTrait] = useState(initialData?.undesiredTrait || '');
  const [microbes, setMicrobes] = useState(
    initialData?.microbes
      ? normalizeSolverMicrobes(initialData.microbes)
      : Array(10).fill(null).map((_, i) => ({
        id: i + 1,
        solverId: i + 1,
        name: '',
        values: {},
        trait: ''
      }))
  );
  
  // Keep existing UI state
  const [expandedSections, setExpandedSections] = useState(['Attributes', 'Traits', 'Microbes']);
  const [errors, setErrors] = useState({});
  const [showHints, setShowHints] = useState(true);
  const [isSolving, setIsSolving] = useState(false);
  const [solveProgress, setSolveProgress] = useState(0);

  // Update the useEffect for initialData changes
  useEffect(() => {
    if (initialData) {
      setSiteNumber(initialData.siteNumber || '');
      setAttributes(initialData.attributes || [
        { name: '', lower: '', upper: '' },
        { name: '', lower: '', upper: '' },
        { name: '', lower: '', upper: '' }
      ]);
      setTraitCount(initialData.traitCount || (initialData.traitNames?.length > 0 ? initialData.traitNames.length : ''));
      setTraitNames(initialData.traitNames || []);
      setDesiredTrait(initialData.desiredTrait || '');
      setUndesiredTrait(initialData.undesiredTrait || '');
      setMicrobes(
        initialData.microbes
          ? normalizeSolverMicrobes(initialData.microbes)
          : Array(10).fill(null).map((_, i) => ({
            id: i + 1,
            solverId: i + 1,
            name: '',
            values: {},
            trait: ''
          }))
      );
    }
  }, [initialData]);

  // Remove the old validation functions that were for animals/producers
  const validateData = () => {
    const newErrors = {};
    
    // Validate site number
    if (!siteNumber || siteNumber < 1 || siteNumber > 3) {
      newErrors.siteNumber = 'Site number must be 1, 2, or 3';
    }
    
    // Validate attributes
    if (attributes.length !== 3) {
      newErrors.attributes = 'Exactly 3 attributes are required';
    }
    
    attributes.forEach((attr, index) => {
      if (!attr.name) {
        newErrors[`attr-${index}-name`] = 'Attribute name is required';
      }
      if (attr.lower < 1 || attr.lower > 10) {
        newErrors[`attr-${index}-lower`] = 'Lower bound must be between 1 and 10';
      }
      if (attr.upper < 1 || attr.upper > 10) {
        newErrors[`attr-${index}-upper`] = 'Upper bound must be between 1 and 10';
      }
      if (attr.lower > attr.upper) {
        newErrors[`attr-${index}-range`] = 'Lower bound must be less than or equal to upper bound';
      }
    });

    // Validate trait names
    if (traitNames.length !== Number(traitCount)) {
      newErrors.traitNames = 'Trait names count must match trait count';
    }

    // Validate desired and undesired traits
    if (!desiredTrait) {
      newErrors.desiredTrait = 'Desired trait is required';
    }
    if (!undesiredTrait) {
      newErrors.undesiredTrait = 'Undesired trait is required';
    }
    if (desiredTrait === undesiredTrait) {
      newErrors.traits = 'Desired and undesired traits must be different';
    }

    // Validate microbes
    if (microbes.length !== 10) {
      newErrors.microbes = 'Exactly 10 microbes are required';
    }

    // Check for duplicate microbe names
    const names = microbes.map(m => m.name.trim()).filter(Boolean);
    const dup = names.find((n, i) => names.indexOf(n) !== i);
    if (dup) {
      newErrors.microbeDuplicates = `Microbe name "${dup}" appears twice`;
    }

    microbes.forEach((microbe, index) => {
      if (!microbe.name) {
        newErrors[`microbe-${index}-name`] = 'Microbe name is required';
      }
      
      // Validate attribute values - only check if they exist, not their range
      attributes.forEach((attr, attrIndex) => {
        const value = microbe.values[attr.name];
        if (value === undefined || value === null || value === '') {
          newErrors[`microbe-${index}-attr-${attrIndex}`] = `Value for ${attr.name} is required`;
        }
      });

      // Validate trait
      if (!microbe.trait) {
        newErrors[`microbe-${index}-trait`] = 'Microbe trait is required';
      } else if (!traitNames.includes(microbe.trait)) {
        newErrors[`microbe-${index}-trait`] = 'Microbe trait must be one of the defined traits';
      }
    });

    setErrors(newErrors);
    const isValid = Object.keys(newErrors).length === 0;
    return isValid;
  };

  // Effect to validate and update parent
  useEffect(() => {
    // Only update QuickReference, not parent navigation/results
    onStateChange?.({
      siteNumber,
      attributes,
      traitCount,
      traitNames,
      desiredTrait,
      undesiredTrait,
      microbes
    });
    validateData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [siteNumber, attributes, traitCount, traitNames, desiredTrait, undesiredTrait, microbes, onStateChange]);

  const renderSiteNumberSection = () => (
    <div className="md:grid md:grid-cols-12 gap-6 p-4 sm:p-3 bg-white/5 rounded-lg border border-blue-800/20 mb-6">
      <div className="md:col-span-4 w-full">
        <label className="block text-sm font-medium text-blue-300 h-14">
          Solve Site Number
          <span className="block text-xs text-blue-400">Enter 1, 2, or 3</span>
        </label>
        <input
          type="text"
          value={siteNumber}
          onChange={(e) => {
            const val = e.target.value;
            // Only allow numbers
            if (/^\d*$/.test(val)) {
              const numVal = parseInt(val);
              if (val === '' || (numVal >= 1 && numVal <= 3)) {
                setSiteNumber(val === '' ? '' : numVal);
              }
            }
          }}
          placeholder="Enter site number"
          className={`w-full h-10 px-3 py-2 bg-gray-900/50 border rounded-lg text-white placeholder-gray-500 focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all ${
            errors.siteNumber ? 'border-red-500' : 'border-blue-800/30'
          }`}
        />
        {errors.siteNumber && renderError(errors.siteNumber)}
      </div>
    </div>
  );

  const renderAttributesSection = () => (
    <motion.div
      initial={false}
      animate={{ backgroundColor: expandedSections.includes('Attributes') ? 'rgba(255, 255, 255, 0.05)' : 'transparent' }}
      className="rounded-xl overflow-hidden mb-6 border border-blue-800/20"
    >
      <div className="flex items-center justify-between p-4 sm:p-3 bg-gradient-to-r from-gray-900/50 to-blue-900/30">
        <div 
          className="flex items-center gap-3 cursor-pointer w-full"
          onClick={() => setExpandedSections(prev => 
            prev.includes('Attributes') 
              ? prev.filter(s => s !== 'Attributes')
              : [...prev, 'Attributes']
          )}
        >
          <span className="text-2xl sm:text-xl">📊</span>
          <div>
            <h3 className="text-xl sm:text-lg font-semibold text-white">Attributes</h3>
            <div className="text-sm sm:text-xs text-blue-400">Enter the three attribute names and their ranges</div>
          </div>
        </div>
      </div>

      <AnimatePresence>
        {expandedSections.includes('Attributes') && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.3 }}
            className="overflow-hidden overscroll-contain"
          >
            <div className="p-4 sm:p-3 space-y-4 bg-gradient-to-br from-gray-900/30 to-blue-900/20">
              {attributes.map((attr, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: -20 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="sm:p-3 p-4 rounded-lg bg-white/5 border border-blue-800/20"
                >
                  <div className="mb-6">
                    <label className="block text-sm font-medium text-blue-300 h-14">
                      Attribute {index + 1} Name
                      <span className="block text-xs text-blue-400">Required</span>
                    </label>
                    <input
                      type="text"
                      value={attr.name}
                      onChange={(e) => {
                        const newAttrs = [...attributes];
                        newAttrs[index] = { ...attr, name: e.target.value };
                        setAttributes(newAttrs);
                      }}
                      placeholder={`Attribute ${index + 1}`}
                      className={`w-full h-10 px-3 py-2 bg-gray-900/50 border rounded-lg text-white placeholder-gray-500 focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all ${
                        errors[`attr-${index}-name`] ? 'border-red-500' : 'border-blue-800/30'
                      }`}
                    />
                    {errors[`attr-${index}-name`] && renderError(errors[`attr-${index}-name`])}
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-blue-300 h-14">
                      Range (1-10)
                      <span className="block text-xs text-blue-400">Enter lower and upper bounds</span>
                    </label>
                    <div className="flex gap-2">
                      <div className="flex-1">
                        <input
                          type="text"
                          value={attr.lower}
                          onChange={(e) => {
                            const val = e.target.value;
                            // Only allow numbers
                            if (/^\d*$/.test(val)) {
                              const numVal = parseInt(val);
                              if (val === '' || (numVal >= 1 && numVal <= 10)) {
                                const newAttrs = [...attributes];
                                newAttrs[index] = { ...attr, lower: val === '' ? '' : numVal };
                                setAttributes(newAttrs);
                              }
                            }
                          }}
                          placeholder="Lower"
                          className={`w-full h-10 px-3 py-2 bg-gray-900/50 border rounded-lg text-white placeholder-gray-500 focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all ${
                            errors[`attr-${index}-lower`] ? 'border-red-500' : 'border-blue-800/30'
                          }`}
                        />
                      </div>
                      <div className="flex-1">
                        <input
                          type="text"
                          value={attr.upper}
                          onChange={(e) => {
                            const val = e.target.value;
                            // Only allow numbers
                            if (/^\d*$/.test(val)) {
                              const numVal = parseInt(val);
                              if (val === '' || (numVal >= 1 && numVal <= 10)) {
                                const newAttrs = [...attributes];
                                newAttrs[index] = { ...attr, upper: val === '' ? '' : numVal };
                                setAttributes(newAttrs);
                              }
                            }
                          }}
                          placeholder="Upper"
                          className={`w-full h-10 px-3 py-2 bg-gray-900/50 border rounded-lg text-white placeholder-gray-500 focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all ${
                            errors[`attr-${index}-upper`] ? 'border-red-500' : 'border-blue-800/30'
                          }`}
                        />
                      </div>
                    </div>
                    {(errors[`attr-${index}-lower`] || errors[`attr-${index}-upper`] || errors[`attr-${index}-range`]) && 
                      renderError(errors[`attr-${index}-lower`] || errors[`attr-${index}-upper`] || errors[`attr-${index}-range`])}
                  </div>
                </motion.div>
              ))}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );

  const renderTraitsSection = () => (
    <motion.div
      initial={false}
      animate={{ backgroundColor: expandedSections.includes('Traits') ? 'rgba(255, 255, 255, 0.05)' : 'transparent' }}
      className="rounded-xl overflow-hidden mb-6 border border-blue-800/20"
    >
      <div className="flex items-center justify-between p-4 sm:p-3 bg-gradient-to-r from-gray-900/50 to-blue-900/30">
        <div 
          className="flex items-center gap-3 cursor-pointer w-full"
          onClick={() => setExpandedSections(prev => 
            prev.includes('Traits') 
              ? prev.filter(s => s !== 'Traits')
              : [...prev, 'Traits']
          )}
        >
          <span className="text-2xl sm:text-xl">🎯</span>
          <div>
            <h3 className="text-xl sm:text-lg font-semibold text-white">Traits</h3>
            <div className="text-sm sm:text-xs text-blue-400">Define traits and select desired/undesired traits</div>
          </div>
        </div>
      </div>

      <AnimatePresence>
        {expandedSections.includes('Traits') && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.3 }}
            className="overflow-hidden overscroll-contain"
          >
            <div className="p-4 sm:p-3 space-y-6 sm:space-y-4 bg-gradient-to-br from-gray-900/30 to-blue-900/20">
              {/* Trait Count Input */}
              <div className="sm:p-3 p-4 rounded-lg bg-white/5 border border-blue-800/20">
                <div className="w-full">
                  <label className="block text-sm font-medium text-blue-300 h-14">
                    Number of Traits
                    <span className="block text-xs text-blue-400">How many distinct traits are there?</span>
                  </label>
                  <input
                    type="text"
                    value={traitCount}
                    onChange={(e) => {
                      const val = e.target.value;
                      // Only allow numbers
                      if (/^\d*$/.test(val)) {
                        const numVal = parseInt(val);
                        if (val === '' || numVal >= 0) {
                          setTraitCount(val === '' ? '' : numVal);
                          // Update trait names array length
                          setTraitNames(prev => {
                            const newNames = [...prev];
                            while (newNames.length < numVal) newNames.push('');
                            while (newNames.length > numVal) newNames.pop();
                            return newNames;
                          });
                        }
                      }
                    }}
                    placeholder="Enter number of traits"
                    className={`w-full h-10 px-3 py-2 bg-gray-900/50 border rounded-lg text-white placeholder-gray-500 focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all ${
                      errors.traitCount ? 'border-red-500' : 'border-blue-800/30'
                    }`}
                  />
                  {errors.traitCount && renderError(errors.traitCount)}
                </div>
              </div>

              {/* Trait Names Inputs */}
              {traitCount > 0 && (
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="sm:p-3 p-4 rounded-lg bg-white/5 border border-blue-800/20"
                >
                  <div className="w-full">
                    <label className="block text-sm font-medium text-blue-300 mb-4 sm:mb-3">
                      Trait Names
                      <span className="block text-xs text-blue-400">Enter the names of each trait</span>
                    </label>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-3">
                      {traitNames.map((trait, index) => (
                        <div key={index}>
                          <input
                            type="text"
                            value={trait}
                            onChange={(e) => {
                              const newNames = [...traitNames];
                              newNames[index] = e.target.value;
                              setTraitNames(newNames);
                            }}
                            placeholder={`Trait ${index + 1}`}
                            className={`w-full h-10 px-3 py-2 bg-gray-900/50 border rounded-lg text-white placeholder-gray-500 focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all ${
                              errors[`trait-${index}`] ? 'border-red-500' : 'border-blue-800/30'
                            }`}
                          />
                          {errors[`trait-${index}`] && renderError(errors[`trait-${index}`])}
                        </div>
                      ))}
                    </div>
                  </div>
                </motion.div>
              )}

              {/* Desired/Undesired Traits Selection */}
              {traitNames.some(t => t) && (
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="space-y-6 sm:space-y-4"
                >
                  {/* Desired Traits */}
                  <div className="p-4 sm:p-3 rounded-lg bg-white/5 border border-blue-800/20">
                    <label className="block text-sm font-medium text-blue-300 mb-4 sm:mb-3">
                      Desired Trait
                      <span className="block text-xs text-blue-400">Select the trait you want to maximize</span>
                    </label>
                    <div className="flex flex-wrap gap-3 sm:gap-2">
                      {traitNames.map((trait, index) => (
                        <motion.button
                          key={index}
                          whileHover={{ scale: 1.05 }}
                          whileTap={{ scale: 0.95 }}
                          onClick={() => setDesiredTrait(trait)}
                          className={`px-4 py-2 sm:px-3 sm:py-1.5 rounded-lg text-sm font-medium transition-all mb-2 ${
                            desiredTrait === trait
                              ? 'bg-blue-600/30 text-blue-300 border border-blue-500/50'
                              : 'bg-gray-900/50 text-gray-400 border border-gray-800/50 hover:bg-gray-800/50'
                          }`}
                        >
                          {trait}
                        </motion.button>
                      ))}
                    </div>
                    {errors.desiredTrait && renderError(errors.desiredTrait)}
                  </div>

                  {/* Undesired Traits */}
                  <div className="p-4 sm:p-3 rounded-lg bg-white/5 border border-blue-800/20">
                    <label className="block text-sm font-medium text-blue-300 mb-4 sm:mb-3">
                      Undesired Trait
                      <span className="block text-xs text-blue-400">Select the trait you want to minimize</span>
                    </label>
                    <div className="flex flex-wrap gap-3 sm:gap-2">
                      {traitNames.map((trait, index) => (
                        <motion.button
                          key={index}
                          whileHover={{ scale: 1.05 }}
                          whileTap={{ scale: 0.95 }}
                          onClick={() => setUndesiredTrait(trait)}
                          className={`px-4 py-2 sm:px-3 sm:py-1.5 rounded-lg text-sm font-medium transition-all mb-2 ${
                            undesiredTrait === trait
                              ? 'bg-red-600/30 text-red-300 border border-red-500/50'
                              : 'bg-gray-900/50 text-gray-400 border border-gray-800/50 hover:bg-gray-800/50'
                          }`}
                        >
                          {trait}
                        </motion.button>
                      ))}
                    </div>
                    {errors.undesiredTrait && renderError(errors.undesiredTrait)}
                  </div>
                </motion.div>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );

  const renderMicrobesSection = () => (
    <motion.div
      initial={false}
      animate={{ backgroundColor: expandedSections.includes('Microbes') ? 'rgba(255, 255, 255, 0.05)' : 'transparent' }}
      className="rounded-xl overflow-hidden mb-6 border border-blue-800/20"
    >
      <div className="flex items-center justify-between p-4 sm:p-3 bg-gradient-to-r from-gray-900/50 to-blue-900/30">
        <div 
          className="flex items-center gap-3 cursor-pointer w-full"
          onClick={() => setExpandedSections(prev => 
            prev.includes('Microbes') 
              ? prev.filter(s => s !== 'Microbes')
              : [...prev, 'Microbes']
          )}
        >
          <span className="text-2xl sm:text-xl">🦠</span>
          <div>
            <h3 className="text-xl sm:text-lg font-semibold text-white">Microbes</h3>
            <div className="text-sm sm:text-xs text-blue-400">Define 10 microbes with their attributes and traits</div>
          </div>
        </div>
      </div>

      <AnimatePresence>
        {expandedSections.includes('Microbes') && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.3 }}
            className="overflow-hidden overscroll-contain"
          >
            <div className="p-4 md:p-4 sm:p-3 space-y-6 bg-gradient-to-br from-gray-900/30 to-blue-900/20">
              {microbes.map((microbe, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="sm:p-3 p-4 rounded-lg bg-white/5 border border-blue-800/20"
                >
                  {/* Microbe Name */}
                  <div className="w-full mb-6">
                    <label className="block text-sm font-medium text-blue-300 mb-4 sm:mb-3">
                      Microbe {index + 1}
                      <span className="block text-xs text-blue-400">Enter the microbe's name and properties</span>
                    </label>
                    <input
                      type="text"
                      value={microbe.name}
                      onChange={(e) => {
                        const newMicrobes = [...microbes];
                        newMicrobes[index] = { ...microbe, name: e.target.value };
                        setMicrobes(newMicrobes);
                      }}
                      placeholder={`Microbe ${index + 1} Name`}
                      className={`w-full h-10 px-3 py-2 bg-gray-900/50 border rounded-lg text-white placeholder-gray-500 focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all ${
                        errors[`microbe-${index}-name`] ? 'border-red-500' : 'border-blue-800/30'
                      }`}
                    />
                    {errors[`microbe-${index}-name`] && renderError(errors[`microbe-${index}-name`])}
                  </div>

                  {/* Attribute Values */}
                  <div className="w-full mb-6">
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      {attributes.map((attr, attrIndex) => (
                        <div key={attrIndex} className="mb-4 md:mb-0">
                          <label className="block text-sm font-medium text-blue-300 mb-2">
                            {attr.name}
                            <span className="block text-xs text-blue-400">Range: {attr.lower}-{attr.upper}</span>
                          </label>
                          <input
                            type="text"
                            value={microbe.values[attr.name] || ''}
                            onChange={(e) => {
                              const val = e.target.value;
                              // Only allow numbers
                              if (/^\d*$/.test(val)) {
                                const numVal = parseInt(val);
                                if (val === '' || !isNaN(numVal)) {
                                  const newMicrobes = [...microbes];
                                  newMicrobes[index] = {
                                    ...microbe,
                                    values: { ...microbe.values, [attr.name]: val === '' ? '' : numVal }
                                  };
                                  setMicrobes(newMicrobes);
                                }
                              }
                            }}
                            placeholder={`Value for ${attr.name}`}
                            className={`w-full h-10 px-3 py-2 bg-gray-900/50 border rounded-lg text-white placeholder-gray-500 focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all ${
                              errors[`microbe-${index}-attr-${attrIndex}`] ? 'border-red-500' : 'border-blue-800/30'
                            }`}
                          />
                          {errors[`microbe-${index}-attr-${attrIndex}`] && 
                            renderError(errors[`microbe-${index}-attr-${attrIndex}`])}
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Trait Selection */}
                  {traitNames.some(t => t) && (
                    <div className="w-full">
                      <label className="block text-sm font-medium text-blue-300 mb-4 sm:mb-3">
                        Trait
                        <span className="block text-xs text-blue-400">Select this microbe's trait</span>
                      </label>
                      <div className="flex flex-wrap gap-3 sm:gap-2">
                        {traitNames.map((trait, traitIndex) => (
                          <motion.button
                            key={traitIndex}
                            whileHover={{ scale: 1.05 }}
                            whileTap={{ scale: 0.95 }}
                            onClick={() => {
                              const newMicrobes = [...microbes];
                              newMicrobes[index] = { ...microbe, trait };
                              setMicrobes(newMicrobes);
                            }}
                            className={`px-4 py-2 sm:px-3 sm:py-1.5 rounded-lg text-sm font-medium transition-all mb-2 ${
                              microbe.trait === trait
                                ? 'bg-blue-600/30 text-blue-300 border border-blue-500/50'
                                : 'bg-gray-900/50 text-gray-400 border border-gray-800/50 hover:bg-gray-800/50'
                            }`}
                          >
                            {trait}
                          </motion.button>
                        ))}
                      </div>
                      {errors[`microbe-${index}-trait`] && renderError(errors[`microbe-${index}-trait`])}
                    </div>
                  )}
                </motion.div>
              ))}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );

  const renderError = (error) => (
    <motion.div
      initial={{ opacity: 0, y: -10 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -10 }}
      className="flex items-center gap-1 text-red-400 text-sm mt-1"
    >
      <ExclamationCircleIcon className="w-4 h-4" />
      {error}
    </motion.div>
  );

  // Update the solve handler to handle CORS
  const handleSolve = async () => {
    // Validate data first
    if (!validateData()) {
      return;
    }
    setIsSolving(true);
    setSolveProgress(0);
    
    try {
      // Transform our data into the API's expected format
      const normalizedMicrobes = normalizeSolverMicrobes(microbes);
      const payload = {
        site: {
          attrs: attributes.map(attr => ({
            name: attr.name,
            lower: parseInt(attr.lower),
            upper: parseInt(attr.upper)
          })),
          desired_trait: desiredTrait,
          undesired_trait: undesiredTrait
        },
        crew: normalizedMicrobes.map(microbe => ({
          id: microbe.solverId,
          name: microbe.name,
          values: attributes.reduce((acc, attr) => ({
            ...acc,
            [attr.name]: parseInt(microbe.values[attr.name])
          }), {}),
          trait: microbe.trait
        }))
      };

      // Same-origin fallback is intentionally disabled here because production routing
      // for /api/seawolf/solve is not authoritative across the simulation/guide hosts.
      const backendUrl = process.env.REACT_APP_API_URL || CLOUD_RUN_SEAWOLF_URL;
      const auth = getAuth();
      const token = auth.currentUser ? await auth.currentUser.getIdToken() : null;
      if (!token) {
        throw new Error('Please sign in to use the Sea Wolf solver.');
      }

      const response = await fetch(`${backendUrl}/api/seawolf/solve`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify(payload)
      });

      if (!response.ok) {
        const responseText = await response.text();
        let errorData = null;

        if (responseText) {
          try {
            errorData = JSON.parse(responseText);
          } catch (_) {
            errorData = responseText;
          }
        }

        throw new Error(formatApiError(errorData, response.status));
      }

      const result = await response.json();
      return handleSuccessfulSolve(result);
      
      function handleSuccessfulSolve(result) {
        // Check if we got a valid response
        if (!result) {
          throw new Error('Empty API response received');
        }

        // Validate required fields
        if (!result.triple) {
          throw new Error(`Invalid API response: missing triple data. Response keys: ${Object.keys(result).join(', ')}`);
        }

        if (typeof result.score !== 'number') {
          throw new Error(`Invalid API response: missing or invalid score. Response keys: ${Object.keys(result).join(', ')}`);
        }

        // Transform the result to include full microbe objects
        const winners = result.triple
          .map(t => normalizedMicrobes.find(m => m.solverId === t.id))
          .filter(Boolean);

        if (winners.length !== result.triple.length) {
          // Keep this warning as it's important for debugging data mismatches
          // console.warn('Some winning microbes not found in original list:', {
          //   expected: result.triple,
          //   found: winners
          // });
        }

        // Notify parent with both input data and solution
        onDataChange({
          siteNumber,
          attributes,
          traitCount,
          traitNames,
          desiredTrait,
          undesiredTrait,
          microbes: normalizedMicrobes,
          solution: {
            ...result,  // Include all API response data
            triple: winners  // Replace triple with full microbe objects
          },
          solved: true
        });

        // Move to results step if available
        if (setActiveStep) {
          setActiveStep(3);
        }
      }

    } catch (error) {
      // Keep error logging for debugging purposes
      // console.error('Error solving ecosystem:', error);
      setErrors(prev => ({
        ...prev,
        solveError: error.message || 'Failed to solve ecosystem. Please try again.'
      }));
    } finally {
      setIsSolving(false);
      setSolveProgress(100);
    }
  };

  return (
    <div className="md:space-y-6 md:p-6 md:bg-gradient-to-br md:from-gray-900/50 md:to-blue-900/30 md:rounded-xl md:border md:border-blue-800/20 md:overflow-hidden">
      {/* Information Panel */}
      {showHints && (
        <motion.div 
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className="bg-blue-900/20 border border-blue-500/20 rounded-lg p-4 sm:p-3 mb-6"
        >
          <div className="flex items-center justify-between mb-2">
            <h4 className="text-lg sm:text-base font-semibold text-blue-300">Sea Wolf Rules</h4>
            <button 
              onClick={() => setShowHints(false)}
              className="text-blue-400 hover:text-blue-300 text-sm"
            >
              Hide
            </button>
          </div>
          <ul className="space-y-2 text-blue-200 text-sm sm:text-xs">
            <li>• Enter the site number (1, 2, or 3)</li>
            <li>• Define three attributes and their ranges (1-10)</li>
            <li>• Enter trait names and select desired/undesired traits</li>
            <li>• Input 10 microbes with their attribute values and traits</li>
            <li>• The solver will find the best combination of 3 microbes</li>
          </ul>
        </motion.div>
      )}

      {/* Site Number Section */}
      {renderSiteNumberSection()}

      {/* Attributes Section */}
      {renderAttributesSection()}

      {/* Traits Section */}
      {renderTraitsSection()}

      {/* Microbes Section */}
      {renderMicrobesSection()}

      {/* Solve Error */}
      {errors.solveError && (
        <div className="mx-2 sm:mx-0 p-3 rounded-lg bg-red-900/30 border border-red-500/40 text-red-300 text-sm flex items-center gap-2">
          <ExclamationCircleIcon className="w-5 h-5 shrink-0" />
          <span>{errors.solveError}</span>
        </div>
      )}

      {/* Solve Button */}
      <div className="px-2 sm:px-0">
        <SolveEcosystemButton
          onSolve={handleSolve}
          isLoading={isSolving}
          progress={solveProgress}
        />
      </div>
    </div>
  );
};

const StyledSeaWolfSolver = (props) => (
  <>
    {/* Desktop version with full containers */}
    <div className="hidden md:block space-y-6 p-6 bg-gradient-to-br from-gray-900/50 to-blue-900/30 rounded-xl border border-blue-800/20 overflow-hidden">
      <ManualEntry {...props} />
    </div>
    
    {/* Mobile version with OcrUpload.js styling */}
    <div className="md:hidden block bg-gradient-to-br from-gray-950/80 to-blue-950/60 rounded-2xl border border-blue-800/20 p-4"
      style={{
        background: 'linear-gradient(135deg, rgba(30,41,59,0.85) 0%, rgba(30,58,138,0.75) 100%)',
        borderRadius: '1rem',
        border: '1px solid rgba(59,130,246,0.12)',
        backdropFilter: 'blur(16px)',
        WebkitBackdropFilter: 'blur(16px)',
        overflow: 'hidden',
      }}>
      <ManualEntry {...props} />
    </div>
  </>
);

export default StyledSeaWolfSolver;
